import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  fetchMe,
  loginAsAgent,
  logout as logoutRequest,
  type MeResponse,
} from '@/lib/auth';
import { apiFetch } from '@/lib/api';

const ME_KEY = ['auth', 'me'] as const;

export function useAuth() {
  const router = useRouter();
  const qc = useQueryClient();

  const meQuery = useQuery<MeResponse | null>({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: false,
  });

  const login = useMutation({
    mutationFn: ({ agentId, password }: { agentId: string; password?: string }) =>
      loginAsAgent(agentId, password),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ME_KEY });
      await router.invalidate();
    },
  });

  const logout = useMutation({
    // The reason rides on the ONE navigation to /login. Navigating to plain
    // /login first and adding ?reason afterwards made the version reload
    // (lib/appVersion.ts) land on /login without it — no inactivity banner.
    mutationFn: async (_vars: { reason?: 'idle' | 'server' } | void) => {
      // Batch ZT (Task 1) — emit a logout activity event BEFORE the
      // session cookie disappears. Best-effort; if it fails we still
      // log out the user.
      const sid =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem('4m.activity.session_id')
          : null;
      await apiFetch('/activity/event', {
        method: 'POST',
        body: JSON.stringify({
          kind: 'logout',
          session_id: sid,
          metadata: { reason: 'user_action' },
        }),
      }).catch(() => undefined);
      return logoutRequest();
    },
    onSuccess: async (_data, vars) => {
      qc.setQueryData(ME_KEY, null);
      const reason = vars ? vars.reason : undefined;
      await router.navigate({ to: '/login', search: reason ? { reason } : {} });
    },
  });

  return {
    me: meQuery.data,
    user: meQuery.data?.user,
    profile: meQuery.data?.profile,
    isLoading: meQuery.isLoading,
    isAuthenticated: !!meQuery.data,
    login,
    logout,
    refetch: meQuery.refetch,
  };
}
